import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createSupervisorChannel } from './createSupervisorChannel';

interface RealtimeSupervisorProps {
  /** When null, the supervisor does nothing (e.g. before login). */
  userId: string | null;
  /** When true, logs every invalidation to console.debug. Defaults to dev mode. */
  debug?: boolean;
}

/**
 * Single source of liveness for the app. Mount once near the root, scoped to
 * the active authenticated user. Opens one Supabase channel that multiplexes
 * listeners for every table in tableMap; each Postgres change becomes a
 * React Query cache invalidation, which causes every consuming useQuery
 * subscriber to refetch and re-render.
 *
 * Renders nothing.
 */
export function RealtimeSupervisor({
  userId,
  debug = import.meta.env.DEV,
}: RealtimeSupervisorProps): null {
  const queryClient = useQueryClient();

  // eslint-disable-next-line contruly/no-direct-use-effect -- Realtime subscription is the canonical useEffect use case (CLAUDE.md "React patterns")
  useEffect(() => {
    if (!userId) return;

    const channel = createSupervisorChannel({
      supabase,
      queryClient,
      userId,
      debug,
    });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, queryClient, debug]);

  return null;
}
